import { useEffect, useState } from 'react'
import { getAuthLabelsPageContent, getCachedAuthLabelsFromSession, themeBrandToPageContentBrand, type PageContentResponse } from '@account/services/authLabels'

export interface UseAuthLabelsResult {
  data: PageContentResponse | null
  loading: boolean
  error: Error | null
}

/**
 * Load auth labels for the current theme brand.
 * Hydrates from session/in-memory cache for fast paint, then always revalidates from the network
 * so stale or partial cache (e.g. missing register.completion) cannot stick forever.
 */
export function useAuthLabels(themeBrand: string): UseAuthLabelsResult {
  const [data, setData] = useState<PageContentResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!themeBrand?.trim()) {
      setData(null)
      setLoading(false)
      return () => {
        cancelled = true
      }
    }
    const brandEnum = themeBrandToPageContentBrand(themeBrand)
    const cached = getCachedAuthLabelsFromSession(brandEnum)
    setData(cached)
    if (cached) {
      setLoading(false)
    } else {
      setLoading(true)
      setError(null)
    }

    getAuthLabelsPageContent(brandEnum, { revalidate: true })
      .then((result) => {
        if (!cancelled && result?.flows) {
          setData(result)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [themeBrand])

  return { data, loading, error }
}
